import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import RefreshIcon from '@mui/icons-material/Refresh';

import { shouldPoll } from 'shared/pollingGate';
import GeoLaunchService from 'services/GeoLaunchService';
import GeoReviewDrawer from './GeoReviewDrawer';
import GeoHistory from './GeoHistory';
import { adUrl, adsetUrl } from './adsManager';
import {
  AD_TYPE_LABEL,
  AdType,
  GEOS,
  Geo,
  GeoBatchStatus,
  GeoCell,
  GeoInfo,
  MatrixResponse,
  QueuedGeoBatch,
  Winner,
  campaignType,
  cellKey,
  isLaunchable,
  isOpen,
  parseAdIds,
  parseCellKey,
} from './geoLaunch';

const POLL_INTERVAL_MS = 5000;
// Only used when the server cannot count the launch workers: a batch first in
// line that no worker has picked up after this long is not "slow" — the worker
// is almost certainly not running. (A batch queued behind another is expected
// to wait; that is shown as its place in line, not as a problem.)
const WORKER_WAIT_WARNING_MS = 2 * 60_000;
const ALL = '__all__';
const ROWS_PER_PAGE_OPTIONS = [25, 50, 100];

type SortKey = 'lastWonAt' | 'trueRoas7d' | 'spend7d' | 'adName';
type Show = 'missing' | 'all';

function errorText(err: unknown): string {
  const anyErr = err as {
    response?: { data?: { error?: string; message?: string } };
    message?: string;
  };
  return (
    anyErr?.response?.data?.error ||
    anyErr?.response?.data?.message ||
    anyErr?.message ||
    'Something went wrong.'
  );
}

const VERDICT_LABEL: Record<string, string> = {
  promote: 'Promote',
  scale: 'Scale',
  keep: 'Keep',
};

function roas(w: Winner) {
  if (w.trueRoas7d == null) return '—';
  return `${w.trueRoas7d.toFixed(2)}×`;
}

function shortDate(iso: string | null) {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const campaignName = (
  info: GeoInfo | undefined,
  id: string | null | undefined,
) => info?.campaigns.find(c => c.id === id)?.name || 'campaign not found';
const adsetName = (info: GeoInfo | undefined, id: string | null | undefined) =>
  info?.adsets.find(s => s.id === id)?.name || 'ad set not found';

/** Campaign on the first line, ad set under it — the shape both location columns share. */
function Location({
  campaign,
  adset,
  href,
}: {
  campaign: string;
  adset: string;
  href?: string | null;
}) {
  const body = (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="body2" sx={{ lineHeight: 1.3 }}>
        {campaign}
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ lineHeight: 1.3 }}
      >
        {adset}
      </Typography>
    </Box>
  );
  return href ? (
    <Link
      href={href}
      target="_blank"
      rel="noopener"
      underline="hover"
      color="inherit"
      sx={{ display: 'block', minWidth: 0 }}
    >
      {body}
    </Link>
  ) : (
    body
  );
}

/**
 * Where each geo that does not have this ad yet would get it: the suggested
 * campaign, and the ad set whose audience and bid settings the new ad set
 * copies. The same suggestion the review drawer starts from — still editable there.
 */
function Proposed({
  winner,
  geoInfo,
}: {
  winner: Winner;
  geoInfo: Map<Geo, GeoInfo>;
}) {
  if (winner.blockedReason) {
    return (
      <Typography variant="caption" color="text.secondary">
        Can't be copied — {winner.blockedReason}
      </Typography>
    );
  }
  const open = GEOS.filter(g => isOpen(winner, g));
  if (!open.length) {
    return (
      <Typography variant="caption" color="text.secondary">
        In every geo
      </Typography>
    );
  }
  return (
    <Stack spacing={0.75}>
      {open.map(g => {
        const info = geoInfo.get(g);
        const s = winner.geos[g].suggestion;
        const campaign = info?.campaigns.find(c => c.id === s?.campaignId);
        const tip = `Landing page: ${s?.link || `none found in live ${g} ads — paste one in review`}`;
        return (
          <Tooltip key={g} title={tip} placement="left">
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              sx={{ minWidth: 0 }}
            >
              <Chip
                size="small"
                label={g}
                sx={{ height: 20, fontSize: 11, flexShrink: 0, mt: 0.25 }}
              />
              {campaign ? (
                <Location
                  campaign={`${campaign.name}${campaign.effectiveStatus === 'ACTIVE' ? '' : ' (paused)'}`}
                  adset={`Ad set: ${adsetName(info, s?.adsetId)}`}
                />
              ) : (
                <Typography variant="caption" color="text.secondary">
                  No matching campaign — pick one in review
                </Typography>
              )}
            </Stack>
          </Tooltip>
        );
      })}
    </Stack>
  );
}

const MATCHED_BY_LABEL: Record<string, string> = {
  label: 'launched by this tool',
  name_and_image: 'same ad name and image',
  name: 'same ad name',
  image: 'same image',
};

/** The geo ads a cell matched, with where they are — the tooltip of every non-empty cell. */
function CopiesList({
  copies,
  info,
}: {
  copies: GeoCell['copies'];
  info: GeoInfo | undefined;
}) {
  return (
    <Box>
      {copies.map(c => (
        <Typography
          key={c.id}
          variant="caption"
          display="block"
          sx={{ mb: 0.5 }}
        >
          {c.name} — {c.effectiveStatus.toLowerCase().replace(/_/g, ' ')} (
          {MATCHED_BY_LABEL[c.matchedBy] || c.matchedBy})
          <br />
          in {campaignName(info, c.campaignId)} › {adsetName(info, c.adsetId)}
        </Typography>
      ))}
    </Box>
  );
}

/**
 * One geo cell: a checkbox when the winner is not there yet — with a "possible
 * copy" flag when something in the geo looks like it but may not be it — a
 * live/paused chip with a checkbox under it to launch it again, or a
 * launching chip.
 */
function Cell({
  winner,
  geo,
  cell,
  info,
  checked,
  onToggle,
}: {
  winner: Winner;
  geo: Geo;
  cell: GeoCell;
  info: GeoInfo | undefined;
  checked: boolean;
  onToggle: () => void;
}) {
  if (cell.status === 'NONE' || cell.status === 'POSSIBLE') {
    if (winner.blockedReason) {
      return (
        <Tooltip title={`Can't be copied — ${winner.blockedReason}`}>
          <span>
            <Checkbox size="small" disabled />
          </span>
        </Tooltip>
      );
    }
    const box = (
      <Checkbox
        size="small"
        checked={checked}
        onChange={onToggle}
        inputProps={{ 'aria-label': `Launch in ${geo}` }}
      />
    );
    if (cell.status === 'NONE')
      return <Tooltip title={`Launch "${winner.adName}" in ${geo}`}>{box}</Tooltip>;
    return (
      <Tooltip
        title={
          <Box>
            <Typography variant="caption" display="block" sx={{ mb: 0.5 }}>
              Possibly already in {geo} — check before launching:
            </Typography>
            <CopiesList copies={cell.copies} info={info} />
          </Box>
        }
      >
        <Stack alignItems="center" spacing={0}>
          {box}
          <Typography
            variant="caption"
            color="warning.main"
            sx={{ lineHeight: 1, mt: -0.5 }}
          >
            possible copy
          </Typography>
        </Stack>
      </Tooltip>
    );
  }
  if (cell.status === 'LAUNCHING')
    return <Chip size="small" color="info" label="Launching" />;
  const live = cell.status === 'LIVE';
  const certain = cell.copies.filter(c => c.strength === 'certain');
  const first = certain[0];
  const title = <CopiesList copies={certain} info={info} />;
  const chip = (
    <Chip
      size="small"
      color={live ? 'success' : 'warning'}
      variant={live ? 'filled' : 'outlined'}
      label={live ? 'Live' : 'Paused'}
    />
  );
  // Already there, but launchable again on purpose: the chip says so, the
  // checkbox under it launches another copy.
  return (
    <Stack alignItems="center" spacing={0}>
      <Tooltip title={title}>
        {info && first ? (
          <Link
            href={adUrl(info.accountId, first.id, {
              campaignId: first.campaignId,
              adsetId: first.adsetId,
            })}
            target="_blank"
            rel="noopener"
          >
            {chip}
          </Link>
        ) : (
          chip
        )}
      </Tooltip>
      {!winner.blockedReason && (
        <Tooltip title={`Already in ${geo} — tick to launch another copy`}>
          <Checkbox
            size="small"
            checked={checked}
            onChange={onToggle}
            inputProps={{ 'aria-label': `Launch again in ${geo}` }}
          />
        </Tooltip>
      )}
    </Stack>
  );
}

/**
 * Geo Launch — copy proven US winners into the UK, Australia and Canada ad
 * accounts.
 *
 * Rows are every ad the Meta Ads Insights pipeline has judged a winner in the
 * US; columns are the geo accounts, read live from Meta. A cell is Live or
 * Paused when the ad is already there (matched by this tool's label, the ad
 * name, or the same image), and a checkbox when it is not. Ticked cells go to
 * the review drawer, where the destination, landing page and copy are
 * confirmed before anything is created.
 */
export default function GeoLaunchTab() {
  const service = useMemo(() => new GeoLaunchService(), []);
  const [view, setView] = useState<'winners' | 'history'>('winners');
  const [data, setData] = useState<MatrixResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [product, setProduct] = useState(ALL);
  const [adType, setAdType] = useState<AdType | typeof ALL>(ALL);
  const [campType, setCampType] = useState(ALL);
  const [show, setShow] = useState<Show>('missing');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('lastWonAt');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(ROWS_PER_PAGE_OPTIONS[0]);

  const [selected, setSelected] = useState<Set<string>>(new Set());
  // US ads added by ID (any US ad, not only winners). Kept in a ref so the
  // matrix read sees the latest list without re-reading on every keystroke.
  const addedRef = useRef<string[]>([]);
  const [addInput, setAddInput] = useState('');
  const [addMessage, setAddMessage] = useState('');
  const [reviewOpen, setReviewOpen] = useState(false);

  const [queued, setQueued] = useState<QueuedGeoBatch[]>([]);
  // When each batch was queued from this browser — the clock for the
  // "waiting for the worker" note, available before the first status poll.
  const [queuedAt, setQueuedAt] = useState<Record<string, number>>({});
  const [statuses, setStatuses] = useState<Record<string, GeoBatchStatus>>({});
  // Launch workers listening, from the last status poll; null = unknown.
  const [workers, setWorkers] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const next = await service.getMatrix(addedRef.current);
      setData(next);
      // An ID that could not be added is dropped, so it is not asked for again.
      const failed = new Set((next.addErrors || []).map(e => e.id));
      addedRef.current = addedRef.current.filter(id => !failed.has(id));
      setAddMessage(
        (next.addErrors || []).map(e => `${e.id}: ${e.error}`).join(' '),
      );
      // A ticked cell that is no longer open (someone launched it meanwhile)
      // would otherwise stay selected with no checkbox left to untick.
      const byId = new Map(next.winners.map(w => [w.adId, w]));
      setSelected(prev => {
        const kept = new Set(
          Array.from(prev).filter(k => {
            const { adId, geo } = parseCellKey(k);
            const w = byId.get(adId);
            return Boolean(w && isLaunchable(w, geo));
          }),
        );
        return kept.size === prev.size ? prev : kept;
      });
    } catch (err) {
      setLoadError(errorText(err));
    } finally {
      setLoading(false);
    }
  }, [service]);

  useEffect(() => {
    load();
  }, [load]);

  const geoInfo = useMemo(
    () => new Map((data?.geos || []).map(g => [g.geo, g])),
    [data],
  );
  const winnersById = useMemo(
    () => new Map((data?.winners || []).map(w => [w.adId, w])),
    [data],
  );
  // Campaign types present among the winners, most common first, with counts
  // for the dropdown. Judged from the US campaign the ad is in now.
  const campaignTypes = useMemo(() => {
    const counts = new Map<string, number>();
    for (const w of data?.winners || []) {
      const t = campaignType(w.usCampaignName);
      counts.set(t, (counts.get(t) || 0) + 1);
    }
    return Array.from(counts.entries()).sort((a, b) => b[1] - a[1]);
  }, [data]);
  const products = useMemo(
    () =>
      Array.from(
        new Set(
          (data?.winners || []).map(w => w.product).filter(Boolean) as string[],
        ),
      ).sort(),
    [data],
  );

  // Rows added by ID are pinned to the top and shown whatever the filters:
  // they were asked for by name.
  const addedRows = useMemo(() => new Set(data?.added || []), [data]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const pinned = (data?.winners || []).filter(w => addedRows.has(w.adId));
    const filtered = (data?.winners || []).filter(
      w =>
        !addedRows.has(w.adId) &&
        (product === ALL || w.product === product) &&
        (adType === ALL || w.adType === adType) &&
        (campType === ALL || campaignType(w.usCampaignName) === campType) &&
        (show === 'all' || GEOS.some(g => isOpen(w, g))) &&
        (!q ||
          w.adName.toLowerCase().includes(q) ||
          (w.campaignName || '').toLowerCase().includes(q)),
    );
    const dir = sortDir === 'asc' ? 1 : -1;
    const sorted = [...filtered].sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      return (
        (typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv))) * dir
      );
    });
    return [...pinned, ...sorted];
  }, [
    data,
    addedRows,
    product,
    adType,
    campType,
    show,
    search,
    sortKey,
    sortDir,
  ]);

  const addById = () => {
    const { ids, bad } = parseAdIds(addInput);
    if (bad.length) {
      setAddMessage(
        `"${bad[0]}" is not an ad ID. Copy the ID from the Ads tab in Ads Manager.`,
      );
      return;
    }
    if (!ids.length) return;
    addedRef.current = Array.from(new Set([...addedRef.current, ...ids]));
    setAddInput('');
    setAddMessage('');
    load();
  };

  const clearAdded = () => {
    addedRef.current = [];
    setAddMessage('');
    load();
  };

  useEffect(() => setPage(0), [product, adType, campType, show, search]);

  const pageRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const toggle = (key: string) =>
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  /** Tick (or untick) every empty cell of one geo on this page. */
  const toggleColumn = (geo: Geo) => {
    // Empty cells only: a possible copy is ticked one by one, after a look.
    const keys = pageRows
      .filter(w => !w.blockedReason && w.geos[geo]?.status === 'NONE')
      .map(w => cellKey(w.adId, geo));
    const allOn = keys.length > 0 && keys.every(k => selected.has(k));
    setSelected(prev => {
      const next = new Set(prev);
      keys.forEach(k => (allOn ? next.delete(k) : next.add(k)));
      return next;
    });
  };

  const sortBy = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'adName' ? 'asc' : 'desc');
    }
  };

  const perGeo = GEOS.map(
    g =>
      [
        g,
        Array.from(selected).filter(k => k.endsWith(`|${g}`)).length,
      ] as const,
  ).filter(([, n]) => n);

  // Poll queued batches until each settles, then re-read the matrix once so
  // the new ads show as Live/Paused from Meta itself.
  const activeIds = useMemo(
    () => queued.map(q => q.batchId).filter(id => !statuses[id]?.done),
    [queued, statuses],
  );
  // Its own clock, not the poll's: polling pauses in a hidden or idle tab, and
  // the "waiting for the worker" note must still appear when time has passed.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!activeIds.length) return undefined;
    const timer = setInterval(() => setNow(Date.now()), 15_000);
    return () => clearInterval(timer);
  }, [activeIds.length]);
  const activeRef = useRef<string[]>([]);
  activeRef.current = activeIds;
  useEffect(() => {
    if (!activeIds.length) return undefined;
    let cancelled = false;
    const tick = async () => {
      if (cancelled || !shouldPoll() || !activeRef.current.length) return;
      const res = await service.getBatchStatus(activeRef.current);
      if (cancelled || !res) return;
      setStatuses(prev => ({ ...prev, ...res.batches }));
      setWorkers(res.workers);
    };
    const timer = setInterval(tick, POLL_INTERVAL_MS);
    tick();
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [activeIds.length, service]);

  // Re-read the matrix (three whole geo accounts from Meta) once, when the
  // last running batch settles — not once per batch, and not at launch time.
  const hadActive = useRef(false);
  useEffect(() => {
    if (activeIds.length) {
      hadActive.current = true;
    } else if (hadActive.current) {
      hadActive.current = false;
      load();
    }
  }, [activeIds.length, load]);

  // Selection as a stable array for the drawer: a new array every render would
  // re-fetch the US ads' copy on every status poll.
  const selectedCells = useMemo(() => Array.from(selected), [selected]);

  const onLaunched = (batches: QueuedGeoBatch[]) => {
    setQueued(prev => [...batches, ...prev]);
    const now = Date.now();
    setQueuedAt(prev => ({
      ...prev,
      ...Object.fromEntries(batches.map(b => [b.batchId, now])),
    }));
    // Show the launched cells as Launching straight away, from what was
    // queued, rather than re-reading every geo account to learn it.
    const launched = new Set(
      batches.flatMap(b => b.ads.map(a => cellKey(a.sourceAdId, b.geo))),
    );
    setData(prev =>
      prev && {
        ...prev,
        winners: prev.winners.map(w =>
          GEOS.some(g => launched.has(cellKey(w.adId, g)))
            ? {
                ...w,
                geos: Object.fromEntries(
                  GEOS.map(g => [
                    g,
                    launched.has(cellKey(w.adId, g))
                      ? { ...w.geos[g], status: 'LAUNCHING' as const }
                      : w.geos[g],
                  ]),
                ) as Winner['geos'],
              }
            : w,
        ),
      },
    );
    setSelected(new Set());
    setReviewOpen(false);
  };

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <Box>
          <Typography variant="body2" color="text.secondary">
            US ads the Meta Ads Insights model has judged winners (promote,
            scale or keep), and whether each is in the UK, Australia and Canada
            accounts. Tick the empty cells to copy an ad into that geo; an ad already
            there can be ticked under its Live or Paused tag to launch another
            copy. To copy any other US ad, add it by its ID.
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={view}
          onChange={(_, v) => v && setView(v)}
        >
          <ToggleButton value="winners">Winners</ToggleButton>
          <ToggleButton value="history">Geo launch history</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {queued.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Launches this session
          </Typography>
          <Stack spacing={1}>
            {queued.map(q => {
              const st = statuses[q.batchId];
              const adsetId = st?.launches.find(l => l.adset_id)?.adset_id;
              const campaignId = st?.launches.find(
                l => l.campaign_id,
              )?.campaign_id;
              const running = !st?.done;
              // Still PENDING means no worker has started it (the worker moves
              // rows to RUNNING the moment it begins).
              const notStarted =
                running &&
                (!st || st.launches.every(l => l.status === 'PENDING'));
              const ahead =
                notStarted && st?.queue?.state === 'queued'
                  ? st.queue.ahead
                  : 0;
              // No worker listening is an outage, whatever the queue says.
              // When the count is unknown, fall back to the clock — but only
              // for a batch first in line: one behind others is just waiting.
              const workerDown =
                notStarted &&
                (workers === 0 ||
                  (workers == null &&
                    !ahead &&
                    now - (queuedAt[q.batchId] ?? now) >
                      WORKER_WAIT_WARNING_MS));
              const failedRows =
                st?.launches.filter(l => l.status === 'FAILED') || [];
              return (
                <Box key={q.batchId}>
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    {running ? <CircularProgress size={14} /> : null}
                    <Chip size="small" label={q.geo} />
                    <Typography variant="body2">
                      {q.ads.length} ad{q.ads.length === 1 ? '' : 's'} →{' '}
                      {q.newCampaign
                        ? `new campaign "${q.newCampaign.name}" (paused)`
                        : q.campaign?.name}{' '}
                      · {q.adsetName}
                    </Typography>
                    {st?.done && (
                      <Chip
                        size="small"
                        color={
                          st.failed
                            ? st.succeeded
                              ? 'warning'
                              : 'error'
                            : 'success'
                        }
                        label={
                          st.failed
                            ? `${st.succeeded} launched, ${st.failed} failed`
                            : `${st.succeeded} launched`
                        }
                      />
                    )}
                    {adsetId && geoInfo.get(q.geo) && (
                      <Link
                        href={adsetUrl(
                          geoInfo.get(q.geo)!.accountId,
                          adsetId,
                          campaignId,
                        )}
                        target="_blank"
                        rel="noopener"
                        variant="body2"
                      >
                        Open in Ads Manager
                      </Link>
                    )}
                  </Stack>
                  {ahead > 0 && !workerDown && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      display="block"
                      sx={{ ml: 3 }}
                    >
                      Queued — {ahead} launch{ahead === 1 ? '' : 'es'} ahead of
                      this one. Launches run one at a time; this starts on its
                      own.
                    </Typography>
                  )}
                  {workerDown && (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      display="block"
                      sx={{ ml: 3 }}
                    >
                      Waiting for the launch worker — nothing has started and
                      nothing has been created yet.{' '}
                      {workers === 0
                        ? 'No launch worker is running; '
                        : 'If this does not change in a few minutes, the ci-worker may be down; '}
                      ask an engineer to check it.
                    </Typography>
                  )}
                  {failedRows.map(r => (
                    <Typography
                      key={r.launch_id}
                      variant="caption"
                      color="error"
                      display="block"
                      sx={{ ml: 3 }}
                    >
                      {r.source_ad_name}: {r.error_message}
                    </Typography>
                  ))}
                </Box>
              );
            })}
          </Stack>
        </Paper>
      )}

      {view === 'history' ? (
        <GeoHistory service={service} geos={data?.geos || []} />
      ) : (
        <>
          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            sx={{ gap: 1.5 }}
          >
            <TextField
              select
              size="small"
              label="Product"
              value={product}
              onChange={e => setProduct(e.target.value)}
              sx={{ minWidth: 180 }}
            >
              <MenuItem value={ALL}>All products</MenuItem>
              {products.map(p => (
                <MenuItem key={p} value={p}>
                  {p}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="US campaign type"
              value={campType}
              onChange={e => setCampType(e.target.value)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value={ALL}>All campaign types</MenuItem>
              {campaignTypes.map(([t, n]) => (
                <MenuItem key={t} value={t}>
                  {t} ({n})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              size="small"
              label="Ad type"
              value={adType}
              onChange={e => setAdType(e.target.value as AdType)}
              sx={{ minWidth: 170 }}
            >
              <MenuItem value={ALL}>All types</MenuItem>
              <MenuItem value="article">{AD_TYPE_LABEL.article}</MenuItem>
              <MenuItem value="direct_response">
                {AD_TYPE_LABEL.direct_response}
              </MenuItem>
            </TextField>
            <TextField
              select
              size="small"
              label="Show"
              value={show}
              onChange={e => setShow(e.target.value as Show)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="missing">Missing from a geo</MenuItem>
              <MenuItem value="all">All winners</MenuItem>
            </TextField>
            <TextField
              size="small"
              label="Search ad or campaign"
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ minWidth: 240 }}
            />
            <Box sx={{ flexGrow: 1 }} />
            {data && (
              <Typography variant="caption" color="text.secondary">
                Read from Meta {new Date(data.generatedAt).toLocaleTimeString()}
              </Typography>
            )}
            <Button
              size="small"
              startIcon={
                loading ? <CircularProgress size={14} /> : <RefreshIcon />
              }
              onClick={load}
              disabled={loading}
            >
              Refresh
            </Button>
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            flexWrap="wrap"
            sx={{ gap: 1.5 }}
          >
            <TextField
              size="small"
              label="Add US ad by ID"
              placeholder="e.g. 120235996025840514, 120251926867490514"
              value={addInput}
              onChange={e => setAddInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') addById();
              }}
              helperText="Any US ad, not only winners — copy the ID from the Ads tab in Ads Manager."
              sx={{ minWidth: 420 }}
            />
            <Button
              size="small"
              variant="outlined"
              onClick={addById}
              disabled={loading || !addInput.trim()}
            >
              Add
            </Button>
            {(data?.added.length || 0) > 0 && (
              <Button size="small" onClick={clearAdded} disabled={loading}>
                Remove added ads ({data?.added.length})
              </Button>
            )}
          </Stack>
          {addMessage && (
            <Alert severity="warning" onClose={() => setAddMessage('')}>
              {addMessage}
            </Alert>
          )}

          {loadError && <Alert severity="error">{loadError}</Alert>}
          {loading && !data && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Reading US winners and the UK, AU and CA ad accounts…
              </Typography>
            </Stack>
          )}

          {data && (
            <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                  <TableRow>
                    <TableCell>
                      <TableSortLabel
                        active={sortKey === 'adName'}
                        direction={sortDir}
                        onClick={() => sortBy('adName')}
                      >
                        US winner
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>US campaign / ad set</TableCell>
                    <TableCell>Product</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Last winning snapshot's 7-day true ROAS">
                        <TableSortLabel
                          active={sortKey === 'trueRoas7d'}
                          direction={sortDir}
                          onClick={() => sortBy('trueRoas7d')}
                        >
                          True ROAS 7d
                        </TableSortLabel>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortKey === 'spend7d'}
                        direction={sortDir}
                        onClick={() => sortBy('spend7d')}
                      >
                        Spend 7d
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortKey === 'lastWonAt'}
                        direction={sortDir}
                        onClick={() => sortBy('lastWonAt')}
                      >
                        Last won
                      </TableSortLabel>
                    </TableCell>
                    {GEOS.map(g => {
                      const info = geoInfo.get(g);
                      const anyActive = info?.campaigns.some(
                        c => c.effectiveStatus === 'ACTIVE',
                      );
                      return (
                        <TableCell key={g} align="center">
                          <Tooltip
                            title={`${info?.name || g}${anyActive ? '' : ' — every campaign is paused right now'}. Click to tick every empty ${g} cell on this page.`}
                          >
                            <Button
                              size="small"
                              onClick={() => toggleColumn(g)}
                              sx={{
                                minWidth: 0,
                                fontWeight: 600,
                                flexDirection: 'column',
                              }}
                            >
                              {g}
                              {!anyActive && (
                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: 'block', lineHeight: 1 }}
                                >
                                  paused
                                </Typography>
                              )}
                            </Button>
                          </Tooltip>
                        </TableCell>
                      );
                    })}
                    <TableCell>Proposed campaign / ad set</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pageRows.map(w => (
                    <TableRow key={w.adId} hover>
                      <TableCell sx={{ maxWidth: 280 }}>
                        <Stack
                          direction="row"
                          spacing={1.5}
                          alignItems="center"
                        >
                          {w.thumbnailUrl ? (
                            <Box
                              component="img"
                              src={w.thumbnailUrl}
                              alt=""
                              sx={{
                                width: 40,
                                height: 40,
                                objectFit: 'cover',
                                borderRadius: 1,
                                flexShrink: 0,
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                bgcolor: 'action.hover',
                                borderRadius: 1,
                                flexShrink: 0,
                              }}
                            />
                          )}
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" noWrap title={w.adName}>
                              {w.adName}
                            </Typography>
                            {w.adIds.length > 1 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                display="block"
                                title="The same creative (name, image and headline) runs as several US ads; they are one row, launched once per geo."
                              >
                                + {w.adIds.length - 1} US duplicate
                                {w.adIds.length === 2 ? '' : 's'}
                              </Typography>
                            )}
                            <Stack
                              direction="row"
                              spacing={0.5}
                              alignItems="center"
                            >
                              <Chip
                                size="small"
                                variant="outlined"
                                label={
                                  w.verdict
                                    ? VERDICT_LABEL[w.verdict] || w.verdict
                                    : 'Added by ID'
                                }
                                sx={{ height: 18, fontSize: 11 }}
                              />
                              {w.mediaType && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                >
                                  {w.mediaType}
                                </Typography>
                              )}
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                title={w.campaignName || ''}
                              >
                                ·{' '}
                                {w.usStatus === 'ACTIVE'
                                  ? 'live in US'
                                  : 'off in US'}
                              </Typography>
                            </Stack>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ minWidth: 220, maxWidth: 260 }}>
                        <Location
                          campaign={w.usCampaignName || '—'}
                          adset={w.usAdsetName || '—'}
                          href={
                            w.usCampaignId && data
                              ? adUrl(data.sourceAccountId, w.adId, {
                                  campaignId: w.usCampaignId,
                                  adsetId: w.usAdsetId,
                                })
                              : null
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {w.product || '—'}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                          display="block"
                        >
                          {AD_TYPE_LABEL[w.adType]}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip
                          title={
                            w.targetTrueRoas != null
                              ? `Target ${w.targetTrueRoas.toFixed(2)}×`
                              : ''
                          }
                        >
                          <span>{roas(w)}</span>
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        {w.spend7d != null
                          ? `$${Math.round(w.spend7d).toLocaleString()}`
                          : '—'}
                      </TableCell>
                      <TableCell>{shortDate(w.lastWonAt)}</TableCell>
                      {GEOS.map(g => (
                        <TableCell key={g} align="center">
                          <Cell
                            winner={w}
                            geo={g}
                            cell={w.geos[g]}
                            info={geoInfo.get(g)}
                            checked={selected.has(cellKey(w.adId, g))}
                            onToggle={() => toggle(cellKey(w.adId, g))}
                          />
                        </TableCell>
                      ))}
                      <TableCell sx={{ minWidth: 300, maxWidth: 360 }}>
                        <Proposed winner={w} geoInfo={geoInfo} />
                      </TableCell>
                    </TableRow>
                  ))}
                  {!pageRows.length && (
                    <TableRow>
                      <TableCell colSpan={7 + GEOS.length}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ py: 2 }}
                        >
                          No winners match these filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={rows.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={e => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(0);
                }}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              />
            </Paper>
          )}

          {selected.size > 0 && (
            <Paper
              elevation={6}
              sx={{ position: 'sticky', bottom: 16, p: 1.5, zIndex: 2 }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body2" fontWeight={600}>
                  {selected.size} launch{selected.size === 1 ? '' : 'es'}{' '}
                  selected
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {perGeo.map(([g, n]) => `${g} ${n}`).join(' · ')}
                </Typography>
                <Box sx={{ flexGrow: 1 }} />
                <Button size="small" onClick={() => setSelected(new Set())}>
                  Clear
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setReviewOpen(true)}
                >
                  Review
                </Button>
              </Stack>
            </Paper>
          )}
        </>
      )}

      {reviewOpen && data && (
        <GeoReviewDrawer
          open={reviewOpen}
          onClose={() => setReviewOpen(false)}
          cells={selectedCells}
          winners={winnersById}
          geos={geoInfo}
          service={service}
          onLaunched={onLaunched}
        />
      )}
    </Stack>
  );
}
